<template>
  <div class="viewer-app" :class="{ 'is-document': documentScroll }">
    <!-- Channel page -->
    <ChannelPage
      v-if="channelHandle"
      :handle="channelHandle"
      :injected-branding="customDomainBranding"
      :api-url="apiUrl"
    />
    <!-- Public video page: /v/:uuid -->
    <VideoPage
      v-else-if="videoUuid"
      :video-uuid="videoUuid"
      :api-url="apiUrl"
    />
    <!-- Tour viewer -->
    <TourViewer
      v-else-if="tourId"
      :tour-id="tourId"
      :api-url="apiUrl"
      :storage-url="storageUrl"
      :initial-theme="initialTheme || undefined"
      :allow-theme-change="true"
      :preview-token="previewToken"
      @tour-loaded="onTourLoaded"
    />
    <!-- No tour/channel — CTA landing page -->
    <div v-else class="redirect-splash">
      <div class="redirect-card">
        <img :src="vteLogoUrl" alt="VirtualTourEasy" class="redirect-logo" />
        <h1 class="redirect-title">VIRTUAL TOUR EASY</h1>
        <p class="redirect-subtitle">Create stunning virtual tours in minutes from AI, 360 pictures or Camera — no technical skills needed.</p>
        <div class="redirect-actions">
          <a href="https://virtualtoureasy.com" class="btn-primary">Discover VirtualTourEasy →</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import TourViewer from './components/TourViewer.vue'
import ChannelPage from './components/ChannelPage.vue'
import VideoPage from './components/VideoPage.vue'
import { themes } from './themes'
import vteLogoUrl from './assets/logo-vte-white.png'
import { useI18n } from 'vue-i18n'
import { setTourMeta } from './composables/useSeoMeta'
import type { ViewerPresentationMode } from './utils/presentationMode'

const { t } = useI18n()

// Get URL parameters
const tourId = ref<string | null>(null)
const previewToken = ref<string | null>(null)
const customDomainBranding = ref<any>(null) // branding injected when on a custom domain
const channelHandle = ref<string | null>(null)
const videoUuid = ref<string | null>(null)
const initialTheme = ref<string | null>(null)
// Channel / video pages are documents, not a full-bleed canvas: they must scroll.
// The tour viewer keeps the locked 100dvh layout.
const documentScroll = ref(false)

/**
 * Let the page scroll for the document-style routes (channel, public video).
 * The global reset locks html/body/#app to 100dvh with overflow:hidden so the
 * 360° canvas never scrolls; without this, channel content below the fold —
 * including the pagination controls — is unreachable.
 */
function enableDocumentScroll() {
  documentScroll.value = true
  document.documentElement.classList.add('document-scroll')
}

// Get environment variables
const apiUrl = import.meta.env.VITE_API_URL || 'https://api-dev.virtualtoureasy.com'
// authUrl removed — viewer uses public /viewer/:id endpoint, no auth needed
const storageUrl = import.meta.env.VITE_STORAGE_URL || 'https://storage.virtualtoureasy.com'

onMounted(async () => {
  const params = new URLSearchParams(window.location.search)
  const path = window.location.pathname

  // Check for channel route: /c/handle
  const channelMatch = path.match(/^\/c\/([a-z0-9][a-z0-9-]{1,39})$/i)
  if (channelMatch) {
    channelHandle.value = channelMatch[1].toLowerCase()
    enableDocumentScroll()
    return
  }

  // Public video page: /v/<uuid> (checked before the generic tour path patterns)
  const videoMatch = path.match(/^\/(?:v|video)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i)
  if (videoMatch) {
    videoUuid.value = videoMatch[1].toLowerCase()
    // Embedded players stay locked to the iframe box; the standalone page scrolls.
    const isEmbed = params.get('embed') === '1' || window.self !== window.top
    if (!isEmbed) enableDocumentScroll()
    return
  }

  // Get tour ID — support query params (?r=, ?v=, ?tourid=) or path-based:
  //   /TOUR_UUID                   — direct tour path
  //   /orgslug/TOUR_UUID           — org-scoped tour path (orgslug is ignored, tour UUID used)
  // Matches UUIDs (hex + dashes, 36 chars) AND legacy alphanumeric IDs (e.g. "AmdxNzZC2nDroe1flup7")
  const pathMatch = path.match(/^\/([a-zA-Z0-9_-]{8,80})$/)
  const orgTourMatch = path.match(/^\/[a-zA-Z0-9_-]+\/([a-zA-Z0-9_-]{8,80})$/)
  tourId.value = params.get('r') || params.get('tourid') || params.get('v') || (pathMatch ? pathMatch[1] : null) || (orgTourMatch ? orgTourMatch[1] : null)
  previewToken.value = params.get('preview_token') || null

  // ── Custom domain detection ────────────────────────────────────────────────
  // If the viewer is loaded from a non-VTE hostname (e.g. tours.customer.com),
  // resolve the domain to the org's channel handle and branding via the API,
  // then redirect to /c/<channel_handle> so the channel page renders.
  const hostname = window.location.hostname
  const isVteDomain = hostname === 'localhost' ||
    hostname.includes('127.0.0.1') ||
    hostname.endsWith('.virtualtoureasy.com') ||
    hostname.endsWith('.vt360.co') ||
    hostname === 'vt360.co' ||
    hostname.endsWith('.pages.dev')

  if (!isVteDomain && !tourId.value && !channelHandle.value) {
    try {
      const res = await fetch(`${apiUrl}/api/branding/domain/${encodeURIComponent(hostname)}`)
      if (res.ok) {
        const data = await res.json() as { branding: any; channel_handle: string | null }
        customDomainBranding.value = data.branding
        if (data.channel_handle) {
          channelHandle.value = data.channel_handle
          enableDocumentScroll()
        }
      }
    } catch { /* silently ignore — falls through to normal tour/error flow */ }
  }
  // ─────────────────────────────────────────────────────────────────────────
  
  // Get theme if valid
  const themeParam = params.get('theme')
  if (themeParam && themes[themeParam]) {
    initialTheme.value = themeParam
  }
})

function onTourLoaded(meta: {
  title: string
  description: string
  image: string
  orgName: string | null
  presentationMode?: ViewerPresentationMode
}) {
  setTourMeta({
    title: meta.title || null,
    description: meta.description || null,
    image: meta.image || null,
    url: window.location.href,
    orgName: meta.orgName,
    presentationMode: meta.presentationMode,
  })
}
</script>

<style>
:root {
  --background-color: #1e293b;
  --text-color: #f8fafc;
  --error-color: #ef4444;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  height: 100%;
  height: 100dvh;
  overflow: hidden;
  -webkit-overflow-scrolling: touch;
}

body {
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu,
    Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  background-color: var(--background-color);
  color: var(--text-color);
}

#app {
  height: 100%;
  height: 100dvh;
  overflow: hidden;
}

.viewer-app {
  width: 100%;
  height: 100vh;
  height: 100dvh; /* Dynamic viewport height — accounts for mobile browser chrome */
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Document-style routes (channel page, standalone video page) ──────────────
 * The defaults above pin everything to the viewport so the 360° canvas never
 * scrolls. Channel/video pages are ordinary documents that grow past the fold,
 * so release the lock on <html>, <body>, #app and .viewer-app for those routes.
 * `documentScroll` in App.vue adds .document-scroll to <html>.
 * ─────────────────────────────────────────────────────────────────────────── */
html.document-scroll,
html.document-scroll body {
  height: auto;
  min-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

html.document-scroll #app,
html.document-scroll .viewer-app.is-document {
  height: auto;
  min-height: 100vh;
  min-height: 100dvh;
  overflow: visible;
}

.redirect-splash {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0f172a 100%);
}

.redirect-card {
  text-align: center;
  max-width: 460px;
  width: 100%;
  padding: 3rem 2.5rem;
  background: rgba(30, 41, 59, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.15);
  border-radius: 20px;
  backdrop-filter: blur(16px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
}

.redirect-logo {
  height: 40px;
  margin-bottom: 2rem;
  object-fit: contain;
  filter: brightness(0) invert(1);
  opacity: 0.9;
}

.redirect-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: #f8fafc;
  margin-bottom: 0.75rem;
  letter-spacing: -0.03em;
  line-height: 1.2;
}

.redirect-subtitle {
  color: #94a3b8;
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
}

.redirect-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
}

.btn-primary {
  display: inline-block;
  width: 100%;
  padding: 0.875rem 2rem;
  background: linear-gradient(135deg, #3b82f6, #6366f1);
  color: white;
  text-decoration: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.2s;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.35);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(99, 102, 241, 0.45);
}

.btn-secondary {
  display: inline-block;
  padding: 0.625rem 1.5rem;
  color: #94a3b8;
  text-decoration: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  transition: color 0.2s;
}

.btn-secondary:hover {
  color: #e2e8f0;
}
</style>
